// AES-256-GCM encryption matching the backend's ApiEncryption utility (the
// Express `decryptRequest` middleware, which decrypts with Node's
// `crypto.createDecipheriv('aes-256-gcm', ...)`). Generic — any client whose
// SSO flow needs to encrypt a payload client-side (e.g. a completion call
// after collecting missing profile fields) can reuse this.
//
// Wire format contract (must match byte-for-byte with the backend):
//   - key: 32 raw bytes (AES-256), base64-encoded for config/storage
//   - iv: 12 random bytes per encryption, base64-encoded
//   - authTag: 16 bytes, base64-encoded, sent SEPARATELY from the ciphertext
//   - encryptedData: base64 ciphertext WITHOUT the auth tag appended
//   - plaintext is JSON.stringify(data), encoded as UTF-8
//
// Web Crypto's AES-GCM encrypt() returns ciphertext with the tag appended at
// the end, unlike Node's `crypto` module which exposes the tag separately via
// getAuthTag() — encryptPayload splits them apart here so the wire format
// matches what the backend's decrypt() expects.

const IV_LENGTH_BYTES = 12;
const AUTH_TAG_LENGTH_BITS = 128; // 16 bytes

function base64ToBytes(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

async function importAesKey(base64Key) {
  const keyBytes = base64ToBytes(base64Key);
  if (keyBytes.length !== 32) {
    throw new Error(
      `Invalid encryption key length: expected 32 bytes, got ${keyBytes.length}`
    );
  }
  // crypto.subtle only exists in a secure context: HTTPS, or localhost.
  // Serving dev over a LAN IP or plain-HTTP tunnel leaves it undefined.
  if (!window.crypto?.subtle) {
    throw new Error(
      'Web Crypto unavailable — open the app over HTTPS or localhost (crypto.subtle requires a secure context)'
    );
  }
  return window.crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, [
    'encrypt',
  ]);
}

// Encrypts `data` (any JSON-serializable value) the same way the backend's
// ApiEncryption.encrypt does, returning { encryptedData, iv, authTag } as
// base64 strings ready to send in the request body.
export async function encryptPayload(data, base64Key) {
  const key = await importAesKey(base64Key);
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(data));

  const cipherBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: AUTH_TAG_LENGTH_BITS },
    key,
    plaintext
  );

  const cipherBytes = new Uint8Array(cipherBuffer);
  const tagLengthBytes = AUTH_TAG_LENGTH_BITS / 8;
  const encryptedBytes = cipherBytes.slice(
    0,
    cipherBytes.length - tagLengthBytes
  );
  const authTagBytes = cipherBytes.slice(cipherBytes.length - tagLengthBytes);

  return {
    encryptedData: bytesToBase64(encryptedBytes),
    iv: bytesToBase64(iv),
    authTag: bytesToBase64(authTagBytes),
  };
}
